import React from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { 
  FileText, 
  Copyright, 
  UserCheck, 
  Ban, 
  CreditCard, 
  AlertTriangle, 
  ShieldAlert, 
  Gavel, 
  Mail,
  ExternalLink,
  ChevronRight,
  ShieldCheck,
  ArrowLeft,
  Activity
} from 'lucide-react';

const TermsOfService: React.FC = () => {
  return (
    <div className="min-h-screen pt-24 pb-20 bg-white relative overflow-hidden font-sans">
      {/* Background Accents */}
      <div className="absolute top-0 right-0 w-full h-[600px] bg-[radial-gradient(circle_at_70%_0%,rgba(168,85,247,0.05)_0%,transparent_70%)] -z-10" />
      <div className="absolute bottom-0 left-0 w-full h-[400px] bg-[radial-gradient(circle_at_20%_100%,rgba(6,182,212,0.03)_0%,transparent_60%)] -z-10" />

      <div className="max-w-5xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="flex flex-col items-start gap-4 mb-12"
        >
          <Link to="/" className="inline-flex items-center gap-2 text-[10px] font-bold text-zinc-400 hover:text-cyan-600 transition-all group uppercase tracking-[0.2em]">
            <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" />
            Home
          </Link>
          
          <div className="space-y-2">
            <h1 className="text-3xl md:text-5xl font-black font-display text-cyan-950 tracking-tighter uppercase">
              Terms of <span className="italic text-cyan-600">Service</span>
            </h1>
            <p className="text-zinc-500 font-medium text-sm leading-relaxed max-w-xl">
              Please read these terms carefully before exploring the Fun Circle Games.
            </p>
          </div>

          <div className="flex items-center gap-4 text-[9px] font-bold uppercase tracking-[0.3em] text-cyan-400 pt-2 border-t border-cyan-600/10 w-full">
            <span>Effective Date: May 2, 2026</span>
            <span className="w-1 h-1 rounded-full bg-cyan-200" />
            <span>Ref: TOS-2026-V2</span>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 gap-8">
          {/* Section 1: Agreement */}
          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-white p-8 md:p-10 rounded-[2rem] border border-cyan-100 shadow-xl shadow-cyan-900/5 hover:shadow-cyan-900/10 transition-all group"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-14 h-14 bg-cyan-600 rounded-2xl flex items-center justify-center shrink-0 shadow-lg shadow-cyan-600/20 group-hover:-translate-y-1 transition-transform">
                <FileText className="w-7 h-7 text-white" />
              </div>
              <div className="space-y-4">
                <h2 className="text-xl font-heavy font-display text-cyan-950 uppercase tracking-tight flex items-center gap-3">
                  1. Agreement to Terms
                  <ChevronRight className="w-4 h-4 text-cyan-200 group-hover:translate-x-1 transition-transform" />
                </h2>
                <div className="space-y-4 text-zinc-600 font-medium leading-relaxed">
                  <p>
                    These Terms of Service constitute a legally binding agreement made between you, whether personally or on behalf of an entity ("you") and Fun Circle Games ("we", "us", or "our"), concerning your access to and use of our website (<a href="https://funcirclegames.com" className="text-cyan-600 hover:underline inline-flex items-center gap-1 font-bold">funcirclegames.com <ExternalLink className="w-3 h-3" /></a>) and the Creator Studio simulation software (collectively, the "Site").
                  </p>
                  <p className="p-6 bg-cyan-50 border border-cyan-100 text-cyan-900 rounded-2xl text-sm font-bold tracking-wide italic">
                    By accessing the Site, you acknowledge that you have read, understood, and agreed to be bound by all of these Terms of Service. IF YOU DO NOT AGREE WITH ALL OF THESE TERMS, THEN YOU ARE EXPRESSLY PROHIBITED FROM USING THE SITE AND MUST DISCONTINUE USE IMMEDIATELY.
                  </p>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Section 2: User Content & Copyright Responsibility */}
          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-red-50/20 p-8 md:p-10 rounded-[2rem] border border-red-100 shadow-xl shadow-red-900/5 hover:shadow-red-900/10 transition-all"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-14 h-14 bg-red-600 rounded-2xl flex items-center justify-center shrink-0 shadow-lg shadow-red-600/20">
                <AlertTriangle className="w-7 h-7 text-white" />
              </div>
              <div className="space-y-6 w-full text-zinc-600">
                <h2 className="text-xl font-heavy font-display text-red-950 uppercase tracking-tight">2. User Responsibility & Copyright</h2>
                
                <div className="space-y-4 font-medium text-sm leading-relaxed">
                  <p className="text-red-600 font-black uppercase tracking-wider text-xs">Critical Music & MIDI Upload Notice</p>
                  <p>
                    You are solely responsible for all content you upload to the Site, including but not limited to <strong className="text-red-950 uppercase tracking-tighter italic">copyrighted songs, MIDI files, recreations, audio clips, and remixes</strong>.
                  </p>
                  <p>
                    You explicitly represent and warrant that you own or have obtained all necessary licenses, rights, and permissions to use any media uploaded to or processed through Creator Studio. 
                    <strong className="text-red-900 underline decoration-red-200 underline-offset-4"> Fun Circle Games does not verify the ownership of uploaded content.</strong>
                  </p>
                  
                  <div className="p-5 bg-white rounded-2xl border border-red-100 space-y-3">
                    <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-cyan-600">Exported Content Responsibility</h4>
                    <p className="text-xs italic">
                      Rendering or exporting a video using a song through our Site does NOT grant you redistribution rights for that music. You are solely responsible for compliance with copyright laws before publishing rendered content on social media or elsewhere.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Section 3: Private Storage & Workspace */}
          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-white p-8 md:p-10 rounded-[2rem] border border-cyan-100 shadow-xl shadow-cyan-900/5 group"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-14 h-14 bg-cyan-600 rounded-2xl flex items-center justify-center shrink-0">
                <ShieldCheck className="w-7 h-7 text-white" />
              </div>
              <div className="space-y-6">
                <h2 className="text-xl font-heavy font-display text-cyan-950 uppercase tracking-tight">3. Private Workspace & Cloud Storage</h2>
                <div className="space-y-4 text-zinc-600 font-medium leading-relaxed text-sm">
                  <p>
                    Fun Circle Games acts as a <strong className="text-cyan-950">private creator workspace</strong>. Uploaded files remain private to your account and are not distributed publicly by default. 
                  </p>
                  <p>
                    While we provide cloud storage for convenience, <strong className="text-red-600">we do not guarantee storage permanence</strong>. Users are responsible for maintaining their own backups. Deleted or corrupted projects may not be recoverable. Inactive projects may be moved to archival storage or deleted after extended periods.
                  </p>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Section 4: Rendering & Export Systems */}
          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-cyan-50/20 p-8 md:p-10 rounded-[2rem] border border-cyan-100"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-14 h-14 bg-cyan-600 rounded-2xl flex items-center justify-center shrink-0 shadow-lg shadow-cyan-600/20">
                <Activity className="w-7 h-7 text-white" />
              </div>
              <div className="space-y-6 w-full">
                <h2 className="text-xl font-heavy font-display text-cyan-950 uppercase tracking-tight">4. Rendering & Performance Disclaimers</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    'Render times are estimated and not guaranteed.',
                    'Export failures may occur due to simulation complexity.',
                    'Resolution availability depends on subscription tier.',
                    'Cloud rendering queue priorities are subject to change.',
                    'Beta engine features may crash or be unstable.',
                    'We reserve the right to remove non-functional export filters.'
                  ].map((item, i) => (
                    <div key={i} className="flex gap-4 p-4 bg-white rounded-2xl border border-cyan-100 items-center">
                      <div className="w-2 h-2 rounded-full bg-cyan-500 shrink-0" />
                      <p className="text-xs font-bold text-cyan-950">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.section>

          {/* Section 5: Subscriptions & Trials */}
          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-white p-8 md:p-10 rounded-[2rem] border border-cyan-100 shadow-xl shadow-cyan-900/5 group"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-14 h-14 bg-cyan-600 rounded-2xl flex items-center justify-center shrink-0">
                <CreditCard className="w-7 h-7 text-white" />
              </div>
              <div className="space-y-6">
                <h2 className="text-xl font-heavy font-display text-cyan-950 uppercase tracking-tight flex items-center gap-3">5. Subscription & Billing Compliance</h2>
                <div className="space-y-4 text-zinc-600 font-medium leading-relaxed text-sm">
                  <p>
                    Paid subscriptions (Creator Pro, Studio Elite) are subject to <strong className="text-cyan-950">auto-renewal</strong> at the end of each billing cycle unless cancelled.
                  </p>
                  <p>
                    If you are on a paid trial, you will be billed the full subscription amount automatically upon trial expiration unless you cancel 24 hours prior to the trial end date.
                  </p>
                  <div className="p-5 bg-red-50 rounded-2xl border border-red-100">
                    <p className="text-red-950 font-bold italic">
                      Refunds are generally not provided for digital goods once accessed or for partial subscription cycles. See our Refund Policy for specific exceptions.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Section 6: DMCA & Copyright Policy */}
          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-white p-8 md:p-10 rounded-[2rem] border border-cyan-100 shadow-xl shadow-cyan-900/5 group"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-14 h-14 bg-cyan-600 rounded-2xl flex items-center justify-center shrink-0">
                <ShieldAlert className="w-7 h-7 text-white" />
              </div>
              <div className="space-y-6">
                <h2 className="text-xl font-heavy font-display text-cyan-950 uppercase tracking-tight">6. Copyright Infringement (DMCA)</h2>
                <div className="space-y-4 text-zinc-600 font-medium leading-relaxed text-sm">
                  <p>
                    We respect the intellectual property rights of others. If you believe your copyright has been infringed upon, please follow our <Link to="/copyright-policy" className="text-cyan-600 font-bold hover:underline">Dedicated Copyright Policy</Link>. 
                  </p>
                  <p>
                    We reserve the right to remove any content and terminate accounts of repeat infringers.
                  </p>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Section 7: Acceptable Use */}
          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-white p-8 md:p-10 rounded-[2rem] border border-cyan-100 shadow-xl shadow-cyan-900/5 group"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-14 h-14 bg-cyan-600 rounded-2xl flex items-center justify-center shrink-0">
                <Ban className="w-7 h-7 text-white" />
              </div>
              <div className="space-y-6">
                <h2 className="text-xl font-heavy font-display text-cyan-950 uppercase tracking-tight">7. Acceptable Use Policy</h2>
                <div className="space-y-4 text-zinc-600 font-medium leading-relaxed text-sm">
                  <p>
                    You may not use the Site to distribute malware, reverse engineer the engine, or upload pirated content. Please review our full <Link to="/acceptable-use" className="text-cyan-600 font-bold hover:underline">Acceptable Use Policy</Link> for detailed prohibited activities.
                  </p>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Section 8 & 9: Liability & Law */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <motion.section 
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white p-8 md:p-10 rounded-[2rem] border border-cyan-100 shadow-xl shadow-cyan-900/5"
            >
              <div className="space-y-6">
                <div className="w-14 h-14 bg-cyan-50 rounded-2xl flex items-center justify-center border border-cyan-100">
                  <ShieldAlert className="w-7 h-7 text-cyan-600" />
                </div>
                <h2 className="text-xl font-heavy font-display text-cyan-950 uppercase tracking-tight font-black italic">8. Liability Limits</h2>
                <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest leading-relaxed">
                  FUN CIRCLE GAMES IS NOT LIABLE FOR REVENUE LOSSES RESULTING FROM RENDER DELAYS, UPLOAD DISPUTES, OR COPYRIGHT STRIKES ON EXPORTED CONTENT.
                </p>
              </div>
            </motion.section>

            <motion.section 
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white p-8 md:p-10 rounded-[2rem] border border-cyan-100 shadow-xl shadow-cyan-900/5"
            >
              <div className="space-y-6">
                <div className="w-14 h-14 bg-cyan-50 rounded-2xl flex items-center justify-center border border-cyan-100">
                  <Gavel className="w-7 h-7 text-cyan-600" />
                </div>
                <h2 className="text-xl font-heavy font-display text-cyan-950 uppercase tracking-tight">9. Governing Law</h2>
                <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest leading-relaxed">
                  THIS AGREEMENT IS GOVERNED BY THE LAWS OF DELAWARE (USA) AND ENGLAND & WALES (UK). DISPUTES SHALL BE RESOLVED VIA BINDING ARBITRATION.
                </p>
              </div>
            </motion.section>
          </div>

          {/* Section 10: Contact Information */}
          <motion.section 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="pt-8"
          >
            <div className="bg-white p-10 md:p-14 rounded-[3rem] border border-cyan-100 shadow-2xl shadow-cyan-950/5 relative overflow-hidden group">
              <div className="absolute -bottom-10 -right-10 w-64 h-64 bg-cyan-500/5 rounded-full blur-[80px]" />
              <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                <div className="space-y-4">
                  <h2 className="text-2xl md:text-4xl font-heavy font-display text-cyan-950 uppercase tracking-tight leading-none">
                    10. Contact <br />
                    <span className="text-cyan-600/60">Us</span>
                  </h2>
                  <p className="text-zinc-500 font-medium text-sm">
                    Questions about these terms? Reach out to our legal department or support desk.
                  </p>
                </div>
                
                <div className="grid grid-cols-1 gap-4">
                  <motion.a 
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    href="mailto:support@funcirclegames.com"
                    className="flex items-center gap-6 p-6 bg-cyan-50 rounded-3xl border border-cyan-100 shadow-xl hover:bg-cyan-100 transition-colors"
                  >
                    <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center border border-cyan-100">
                      <Mail className="w-6 h-6 text-cyan-600" />
                    </div>
                    <div>
                      <span className="block text-[10px] font-black uppercase text-cyan-400 tracking-[0.2em] mb-1">Legal Support</span>
                      <span className="text-lg font-bold text-cyan-950">support@funcirclegames.com</span>
                    </div>
                  </motion.a>
                </div>
              </div>
            </div>
          </motion.section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;


